import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircuitVm } from 'api/picking/circuit.vm';
import { GetCircuitsVm } from 'api/picking/get-circuits.vm';
import { getCircuits } from 'api/picking/get-circuits';
import { createCircuit } from 'api/picking/create-circuit';
import { assignTeamToCircuit } from 'api/picking/assign-team-to-circuit';
import { assignPreparationAreaToCircuit } from 'api/picking/assign-preparation-area-to-circuit';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { useSnackbar } from 'hooks/use-snackbar';
import CreateCircuitDialog from 'pages/picking/CreateCircuitDialog';

const CircuitsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [circuits, setCircuits] = useState<CircuitVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const warehouseId = searchParams.get('warehouseId');
  const snackbar = useSnackbar();

  const fetchCircuits = (offset: string | null, reset: boolean = false) => {
    if (!warehouseId) return;
    setLoading(true);

    getCircuits(offset || '', warehouseId, {
      success: (response: GetCircuitsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setCircuits(reset ? response.objects : [...circuits, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter circuitos:', err);
        snackbar.show('Falha ao carregar circuitos', null, 'error');
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
        if (response.objects.length && !warehouseId) {
          setSearchParams((prev) => {
            prev.set('warehouseId', response.objects[0].id);
            return prev;
          });
        }
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
        snackbar.show('Falha ao carregar armazéns', null, 'error');
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    if (warehouseId) {
      fetchCircuits(null, true);
    } else {
      setCircuits([]);
    }
    // eslint-disable-next-line
  }, [warehouseId]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchCircuits(nextOffset);
  };

  const handleCreateCircuit = (warehouseId: string, name: string, priority: number) => {
    setCreating(true);
    createCircuit(warehouseId, name, priority, {
      success: () => {
        snackbar.show('Circuito criado com sucesso!', null, 'success');
        setCreateDialogOpen(false);
        setCreating(false);
        fetchCircuits(null, true);
      },
      error: (err) => {
        console.error('Falha ao criar circuito:', err);
        snackbar.show('Falha ao criar circuito', null, 'error');
        setCreating(false);
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Circuitos
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)} disabled={!warehouseId}>
          Criar Circuito
        </Button>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Armazém</InputLabel>
              <Select
                value={warehouseId || ''}
                label="Armazém"
                onChange={(e) =>
                  setSearchParams((prev) => {
                    prev.set('warehouseId', e.target.value);
                    return prev;
                  })
                }
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Prioridade</TableCell>
              <TableCell>Equipa Atribuída</TableCell>
              <TableCell>Área de Preparação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {circuits.map((circuit) => (
              <TableRow key={circuit.id} hover onClick={() => navigate(`/shipping/circuits/${circuit.id}`)} sx={{ cursor: 'pointer' }}>
                <TableCell>{circuit.id}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {circuit.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{circuit.priority}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={circuit.teamName ? 'text.primary' : 'text.secondary'}>
                    {circuit.teamName || 'Sem equipa'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={circuit.preparationAreaId ? 'text.primary' : 'text.secondary'}>
                    {circuit.preparationAreaId || 'Sem área'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : circuits.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais circuitos para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhum circuito disponível
          </Typography>
        )}
      </Box>

      <CreateCircuitDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateCircuit}
        loading={creating}
        preselectedWarehouseId={warehouseId}
        preselectedWarehouseName={warehouses.find((w) => w.id === warehouseId)?.name}
      />
    </Box>
  );
};

export default CircuitsPage;
